import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft,
    Heart,
    Target,
    Sparkles,
    BarChart3,
    MessageSquare,
    HelpCircle,
    Play,
    ChevronDown,
    Brain,
    CheckCircle2,
    XCircle,
    Trophy,
    RotateCw,
    Timer,
    Zap,
    Loader2,
    BookOpen,
    Medal,
    Newspaper,
    Pin,
    Flame,
    Megaphone,
    AlertTriangle,
    Info,
    Quote,
    ExternalLink,
    FolderOpen,
    Eye,
} from "lucide-react";
import api from "../lib/api";
import ResourcesSection from "./ResourcesSection";

/**
 * Iter-31 — Fiche FORMATION d'une association, extraite de la page /associations/:id
 * pour être réutilisée telle quelle dans le backoffice partenaire (onglet
 * "Vue ambassadeur"). En `previewMode` (compte association) :
 *   - aucun tracking de lecture (sections / news) n'est envoyé
 *   - le quiz est jouable mais le backend n'attribue aucun point (award_reason=preview)
 */
const ALL_SECTIONS = [
    { id: "news", label: "Actualités", icon: Newspaper },
    { id: "resources", label: "Ressources", icon: FolderOpen },
    { id: "history", label: "Histoire", icon: BookOpen },
    { id: "mission", label: "Mission", icon: Target },
    { id: "actions", label: "Actions", icon: Sparkles },
    { id: "victories", label: "Réussites", icon: Medal },
    { id: "chiffres_cles", label: "Chiffres clés", icon: BarChart3 },
    { id: "argumentaire", label: "Argumentaire terrain", icon: MessageSquare },
    { id: "faq", label: "FAQ / Objections", icon: HelpCircle },
    { id: "quiz", label: "Quiz de connaissance", icon: Brain },
];

// Iter-27.35 — meta pour catégories d'actualité (la catégorie "gratitude" est
// désormais filtrée out de la page formation : elle vit dans /espace-partenaires).
const NEWS_CATEGORY_META = {
    urgence: { label: "URGENCE", color: "#ef4444", bg: "rgba(239,68,68,0.16)", border: "rgba(239,68,68,0.45)", Icon: Flame },
    alerte: { label: "ALERTE", color: "#fb923c", bg: "rgba(251,146,60,0.16)", border: "rgba(251,146,60,0.45)", Icon: AlertTriangle },
    campagne: { label: "CAMPAGNE", color: "#a78bfa", bg: "rgba(167,139,250,0.16)", border: "rgba(167,139,250,0.45)", Icon: Megaphone },
    temoignage: { label: "TÉMOIGNAGE", color: "#fbbf24", bg: "rgba(251,191,36,0.16)", border: "rgba(251,191,36,0.45)", Icon: Quote },
    info: { label: "INFO", color: "#7dd3fc", bg: "rgba(125,211,252,0.16)", border: "rgba(125,211,252,0.45)", Icon: Info },
};

function FormattedText({ text }) {
    if (!text) return <p className="text-white/40 italic">Pas encore renseigné.</p>;
    // Split on lines starting with "- " for bullet rendering. Keep paragraphs otherwise.
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const isList = lines.length > 1 && lines.every((l) => l.trim().startsWith("- "));
    if (isList) {
        return (
            <ul className="space-y-2">
                {lines.map((l, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/85 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-shariti-orange mt-1.5 shrink-0" />
                        <span>{l.replace(/^-\s*/, "")}</span>
                    </li>
                ))}
            </ul>
        );
    }
    return (
        <div className="space-y-3 text-sm text-white/85 font-light leading-relaxed">
            {lines.map((p, i) => (
                <p key={i}>{p}</p>
            ))}
        </div>
    );
}

function VictoriesList({ items }) {
    if (!items || items.length === 0) {
        return (
            <div className="text-center py-10" data-testid="victories-empty">
                <div className="w-16 h-16 rounded-3xl bg-shariti-orange/10 flex items-center justify-center mx-auto mb-4">
                    <Medal className="w-7 h-7 text-shariti-orange/70" />
                </div>
                <p className="text-white/55 italic text-sm max-w-md mx-auto leading-relaxed">
                    Pas encore de réussites renseignées. L'admin peut générer cette section depuis le backoffice.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <p className="text-sm text-white/55 font-light leading-relaxed mb-2">
                Voici les <span className="text-shariti-orange-light font-semibold">victoires concrètes</span> sur lesquelles tu peux t'appuyer terrain — c'est <span className="text-white font-semibold">la preuve par l'action</span> face aux passants qui doutent de l'impact réel de l'association.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
                {items.map((v, idx) => (
                    <motion.div
                        key={`${v.title}-${idx}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.04 }}
                        className="relative rounded-2xl p-4 border border-shariti-orange/20 bg-gradient-to-br from-shariti-orange/8 to-transparent"
                        data-testid={`victory-card-${idx}`}
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                                <Trophy className="w-4 h-4 text-shariti-orange-light" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap mb-1.5">
                                    <h4 className="text-base font-extrabold text-white leading-tight">
                                        {v.title}
                                    </h4>
                                    {v.year && (
                                        <span className="text-[10px] font-bold tracking-wider uppercase text-shariti-orange-light px-2 py-0.5 rounded-full bg-shariti-orange/12 shrink-0">
                                            {v.year}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[13px] text-white/70 font-light leading-relaxed mb-2">
                                    {v.description}
                                </p>
                                {v.impact && (
                                    <div className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-extrabold text-shariti-orange-light bg-shariti-orange/10 px-2.5 py-1 rounded-full border border-shariti-orange/25">
                                        <Zap className="w-3 h-3" />
                                        {v.impact}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}


function formatRelativeDate(iso) {
    if (!iso) return "";
    try {
        const dt = new Date(iso);
        const now = new Date();
        const diffH = (now - dt) / 3600000;
        if (diffH < 1) return "à l'instant";
        if (diffH < 24) return `il y a ${Math.floor(diffH)} h`;
        const diffD = Math.floor(diffH / 24);
        if (diffD < 7) return `il y a ${diffD} j`;
        return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "";
    }
}

function NewsList({ items }) {
    if (!items || items.length === 0) {
        return (
            <div className="text-center py-10" data-testid="news-empty">
                <div className="w-16 h-16 rounded-3xl bg-shariti-orange/10 flex items-center justify-center mx-auto mb-4">
                    <Newspaper className="w-7 h-7 text-shariti-orange/70" />
                </div>
                <p className="text-white/55 italic text-sm max-w-md mx-auto leading-relaxed">
                    Pas d'actualité pour le moment. L'admin peut en publier depuis le backoffice.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <p className="text-sm text-white/55 font-light leading-relaxed mb-2">
                Les <span className="text-shariti-orange-light font-semibold">actualités fraîches</span> de l'association — utilise-les comme arguments concrets sur le terrain cette semaine.
            </p>
            <div className="space-y-3">
                {items.map((n, idx) => {
                    const meta = NEWS_CATEGORY_META[n.category] || NEWS_CATEGORY_META.info;
                    const Icon = meta.Icon;
                    return (
                        <motion.div
                            key={n.news_id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25, delay: idx * 0.04 }}
                            className={`relative rounded-2xl border transition-all overflow-hidden ${
                                n.pinned
                                    ? "border-shariti-orange/35 bg-gradient-to-br from-shariti-orange/10 to-shariti-orange/2"
                                    : "border-white/10 bg-white/4"
                            }`}
                            data-testid={`news-card-${idx}`}
                        >
                            {/* Iter-30.26 — Image hero affichée aussi sur la page asso,
                                cohérent avec le dashboard. Si pas d'image_url, la
                                section est simplement omise et la card commence par
                                le contenu (look avant le fix). */}
                            {n.image_url && (
                                <div className="relative w-full h-44 sm:h-52 bg-shariti-deep-black overflow-hidden">
                                    <img
                                        src={n.image_url}
                                        alt=""
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.parentElement.style.display = "none";
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-shariti-deep-black/85 via-shariti-deep-black/0 to-transparent pointer-events-none" />
                                </div>
                            )}
                            <div className="p-4">
                            {n.pinned && (
                                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.2em] text-shariti-orange-light z-10">
                                    <Pin className="w-2.5 h-2.5" />
                                    Épinglé
                                </span>
                            )}
                            {!n.is_read && !n.pinned && (
                                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.2em] text-red-300 z-10">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                    Nouveau
                                </span>
                            )}
                            <div className="flex items-start gap-3">
                                <div
                                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border"
                                    style={{ background: meta.bg, borderColor: meta.border }}
                                >
                                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                        <span
                                            className="text-[9px] font-extrabold tracking-[0.22em] uppercase px-2 py-0.5 rounded-full"
                                            style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
                                        >
                                            {meta.label}
                                        </span>
                                        <span className="text-[10px] text-white/40 font-light">
                                            {formatRelativeDate(n.published_at)}
                                        </span>
                                    </div>
                                    <h4 className="text-base font-extrabold text-white leading-tight mb-1.5">
                                        {n.title}
                                    </h4>
                                    <p className="text-[13px] text-white/70 font-light leading-relaxed whitespace-pre-line">
                                        {n.body}
                                    </p>
                                    {n.attachment_url && (
                                        <a
                                            href={n.attachment_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 mt-2.5 text-[11px] font-bold text-shariti-orange-light hover:text-shariti-orange transition-colors"
                                        >
                                            <ExternalLink className="w-3 h-3" />
                                            Lire plus
                                        </a>
                                    )}
                                </div>
                            </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}



function FaqAccordion({ items }) {
    const [open, setOpen] = useState(0);
    if (!items || items.length === 0) {
        return <p className="text-white/40 italic text-sm">Pas encore de questions.</p>;
    }
    return (
        <div className="space-y-2">
            {items.map((f, i) => {
                const isOpen = open === i;
                return (
                    <div
                        key={i}
                        className={`rounded-2xl border transition-all ${
                            isOpen
                                ? "border-shariti-orange/40 bg-shariti-orange/5"
                                : "border-white/10 bg-white/5"
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            data-testid={`faq-toggle-${i}`}
                            className="w-full flex items-start gap-3 px-4 py-3 text-left"
                        >
                            <span className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-shariti-orange-light mt-1">
                                Q{i + 1}
                            </span>
                            <span className="flex-1 text-sm font-bold text-white pr-2 leading-tight">
                                {f.question}
                            </span>
                            <ChevronDown
                                className={`w-4 h-4 text-white/60 shrink-0 transition-transform ${
                                    isOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <p className="px-4 pb-4 pt-0 text-sm text-white/80 font-light leading-relaxed">
                                        {f.answer}
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
}

function VideoEmbed({ url }) {
    if (!url) return null;
    // Direct video file (uploaded MP4/WebM) → native HTML5 video player
    const isDirect = /\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(url) || url.includes("/api/files/");
    // YouTube watch?v= or youtu.be → embed/
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    const ytSrc = ytMatch ? `https://www.youtube.com/embed/${ytMatch[1]}` : null;
    return (
        <div className="relative w-full aspect-video rounded-3xl overflow-hidden bg-shariti-deep-black border border-white/10">
            {isDirect ? (
                <video
                    src={url}
                    controls
                    playsInline
                    className="absolute inset-0 w-full h-full bg-black"
                    data-testid="association-video"
                >
                    Ton navigateur ne supporte pas la lecture vidéo HTML5.
                </video>
            ) : (
                <iframe
                    src={ytSrc || url}
                    title="Vidéo de présentation"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="association-video"
                />
            )}
        </div>
    );
}

function QuizHistoryMini({ associationId, refreshKey }) {
    const [history, setHistory] = useState(null);
    useEffect(() => {
        api.get(`/associations/${associationId}/quiz/history`)
            .then((r) => setHistory(r.data.attempts || []))
            .catch(() => setHistory([]));
    }, [associationId, refreshKey]);

    if (!history || history.length === 0) return null;
    const max = 100;
    return (
        <div className="bg-white/[0.04] rounded-2xl p-3 sm:p-4" data-testid="quiz-history">
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/45">
                    Tes {history.length} dernière{history.length > 1 ? "s" : ""} tentative{history.length > 1 ? "s" : ""}
                </p>
                <p className="text-[10px] font-bold text-shariti-orange-light">
                    Meilleur : {Math.max(...history.map((h) => h.score))}%
                </p>
            </div>
            {/* Tiny inline chart — bars */}
            <div className="flex items-end gap-1.5 h-14 mb-2">
                {history.map((h, i) => {
                    const heightPct = Math.max(8, (h.score / max) * 100);
                    const passed = h.passed;
                    return (
                        <div
                            key={i}
                            className="flex-1 flex flex-col justify-end items-center group"
                            title={`${h.score}% • ${h.time_seconds ? `${h.time_seconds}s` : ""}`}
                        >
                            <span className="text-[9px] font-extrabold text-white/55 mb-1 group-hover:text-white">
                                {h.score}
                            </span>
                            <div
                                className={`w-full rounded-t-md transition-all ${
                                    passed ? "bg-shariti-orange" : "bg-shariti-yellow/60"
                                }`}
                                style={{ height: `${heightPct}%` }}
                            />
                        </div>
                    );
                })}
            </div>
            <p className="text-[10px] text-white/35 text-center font-light">
                Plus tu pratiques, plus ta connaissance se renforce.
            </p>
        </div>
    );
}

function QuizSection({ associationId, previewMode = false }) {
    const [data, setData] = useState(null); // {available, questions, last_attempt, best_score, version}
    const [answers, setAnswers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    // Iter-12 — snapshot the questions actually shown to the user so the result panel
    // ALWAYS displays the questions they answered, even if the quiz gets regenerated.
    const [shownQuestions, setShownQuestions] = useState([]);
    // Iter-13 — speed-bonus timer
    const SPEED_BONUS_S = 60;
    const [startedAt, setStartedAt] = useState(null);
    const [now, setNow] = useState(Date.now());

    const elapsed = startedAt ? Math.floor((now - startedAt) / 1000) : 0;
    const timeLeft = Math.max(0, SPEED_BONUS_S - elapsed);
    const bonusEligible = startedAt !== null && elapsed <= SPEED_BONUS_S;

    // Tick the clock every 250ms while the quiz is being answered
    useEffect(() => {
        if (!startedAt || result || !data?.available) return undefined;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [startedAt, result, data]);

    const reload = () => {
        api.get(`/associations/${associationId}/quiz`).then((r) => {
            setData(r.data);
            setAnswers(Array(r.data.questions?.length || 0).fill(-1));
            setResult(null);
            setShownQuestions(r.data.questions || []);
            setStartedAt(r.data.available && r.data.questions?.length ? Date.now() : null);
            setNow(Date.now());
        });
    };
    useEffect(() => {
        reload();
    }, [associationId]);

    const [refreshing, setRefreshing] = useState(false);
    const refreshQuiz = async () => {
        setRefreshing(true);
        try {
            const r = await api.post(`/associations/${associationId}/quiz/refresh`);
            setData(r.data);
            setShownQuestions(r.data.questions || []);
            setAnswers(Array(r.data.questions?.length || 0).fill(-1));
            setResult(null);
            setStartedAt(Date.now());
            setNow(Date.now());
            toast.success("Nouvelle série générée — bonne chance !");
        } catch (e) {
            if (e.response?.status === 429) {
                toast.error(e.response.data?.detail || "Patiente un peu avant la prochaine série.");
            } else {
                toast.error(e.response?.data?.detail || "Échec de la génération");
            }
        } finally {
            setRefreshing(false);
        }
    };

    const submit = async () => {
        if (answers.some((a) => a < 0)) {
            toast.error("Réponds à toutes les questions");
            return;
        }
        setSubmitting(true);
        const timeSeconds = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : null;
        try {
            const r = await api.post(
                `/associations/${associationId}/quiz/submit`,
                {
                    answers,
                    version: data?.version,
                    time_seconds: timeSeconds,
                    served_indexes: data?.served_indexes,
                },
            );
            setResult(r.data);
            if (r.data.passed) {
                const bonus = r.data.speed_bonus_earned ? " (+ bonus rapidité)" : "";
                let mainMsg = `Validé ! ${r.data.score}%`;
                if (r.data.points_earned > 0) {
                    mainMsg += ` • +${r.data.points_earned} pts`;
                } else if (r.data.award_reason === "cooldown_active") {
                    mainMsg += " • Pas de point cette fois (déjà validé dans les 24h)";
                } else if (r.data.award_reason === "max_passes_reached") {
                    mainMsg += " • Pas de point (quiz déjà maîtrisé)";
                }
                toast.success(mainMsg + bonus);
                if (r.data.points_earned > 0) {
                    window.dispatchEvent(new CustomEvent("shariti:points-changed"));
                }
            } else {
                toast.error(`${r.data.score}% — il faut 70 % minimum`);
            }
        } catch (e) {
            if (e.response?.status === 409) {
                // Iter-14 — only happens when the quiz has been heavily modified (different
                // question count) AND the user's version is too old to be in history.
                toast.error(
                    e.response.data?.detail ||
                        "Le quiz a été significativement modifié. On recharge la version la plus récente.",
                    { duration: 5000 },
                );
                reload();
            } else {
                toast.error(e.response?.data?.detail || "Erreur");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!data) {
        return <p className="text-white/40 italic text-sm">Chargement...</p>;
    }

    if (!data.available) {
        return (
            <div className="text-center py-8 px-4">
                <div className="w-12 h-12 rounded-2xl bg-shariti-orange/15 flex items-center justify-center mx-auto mb-3">
                    <Brain className="w-5 h-5 text-shariti-orange" />
                </div>
                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                    Quiz à venir
                </p>
                <p className="text-sm text-white/65 font-light max-w-xs mx-auto">
                    Le quiz de cette association n'est pas encore disponible. Reviens bientôt.
                </p>
            </div>
        );
    }

    if (result) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
                data-testid="asso-quiz-result"
            >
                <QuizHistoryMini associationId={associationId} refreshKey={result.score} />
                <div
                    className={`rounded-3xl p-6 text-center relative overflow-hidden ${result.passed ? "bg-shariti-orange" : "bg-shariti-surface"}`}
                >
                    {result.speed_bonus_earned && (
                        <span
                            className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-extrabold tracking-[0.2em] uppercase bg-shariti-yellow/95 text-shariti-black px-2.5 py-1 rounded-full shadow-lg"
                            data-testid="speed-bonus-badge"
                        >
                            <Zap className="w-3 h-3" />
                            Rapidité +15 pts
                        </span>
                    )}
                    <Trophy className="w-12 h-12 mx-auto mb-2 text-white" />
                    <p className="text-5xl font-extrabold text-white mb-1">{result.score}%</p>
                    <p className="text-white/90 font-bold text-sm">
                        {result.correct} / {result.total} bonnes réponses
                        {result.time_seconds != null && (
                            <span className="text-white/65 font-normal">
                                {" "}· {result.time_seconds}s
                            </span>
                        )}
                    </p>
                    {result.passed && result.points_earned > 0 && (
                        <p className="text-white/80 text-xs mt-1">+{result.points_earned} pts</p>
                    )}
                    {previewMode && (
                        <p className="text-white/75 text-xs mt-1 font-bold">
                            Mode aperçu — aucun point attribué
                        </p>
                    )}
                </div>
                <div className="space-y-2">
                    {result.feedback.map((f, i) => (
                        <div
                            key={i}
                            className="bg-white/5 border border-white/10 rounded-2xl p-3 flex gap-3"
                        >
                            {f.is_correct ? (
                                <CheckCircle2 className="w-5 h-5 text-shariti-orange shrink-0 mt-0.5" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-white mb-1">{f.question}</p>
                                {!f.is_correct && (
                                    <p className="text-xs text-white/60 mb-1">
                                        Bonne réponse :{" "}
                                        <span className="text-shariti-orange-light font-bold">
                                            {f.correct_answer}
                                        </span>
                                    </p>
                                )}
                                {f.explanation && (
                                    <p className="text-xs text-white/55 italic leading-relaxed">
                                        {f.explanation}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setAnswers(Array(data.questions.length).fill(-1));
                            setResult(null);
                            setStartedAt(Date.now());
                            setNow(Date.now());
                        }}
                        data-testid="asso-quiz-retry"
                        className="glass-card rounded-2xl py-3 font-extrabold text-white text-sm hover:bg-white/5 flex items-center justify-center gap-2"
                    >
                        <RotateCw className="w-4 h-4" />
                        Refaire ce quiz
                    </button>
                    <button
                        type="button"
                        onClick={refreshQuiz}
                        disabled={refreshing}
                        data-testid="asso-quiz-refresh"
                        className="rounded-2xl py-3 font-extrabold text-white text-sm transition-all flex items-center justify-center gap-2 bg-shariti-orange/20 border border-shariti-orange/40 hover:bg-shariti-orange/30 disabled:opacity-60"
                    >
                        {refreshing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4 text-shariti-orange-light" />
                        )}
                        Nouvelle série de questions
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <div className="space-y-4" data-testid="asso-quiz-form">
            <QuizHistoryMini associationId={associationId} refreshKey={data?.version} />
            {previewMode ? (
                /* Iter-31 — Aperçu partenaire : le quiz est jouable mais sans points */
                <div
                    className="rounded-2xl px-4 py-3 flex items-center gap-3 bg-shariti-yellow/10 border border-shariti-yellow/30"
                    data-testid="quiz-preview-banner"
                >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-shariti-yellow/20 text-shariti-yellow">
                        <Eye className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-bold text-white leading-snug">
                        Mode aperçu — tu joues le quiz{" "}
                        <span className="text-shariti-yellow">exactement comme tes ambassadeurs</span>, sans gagner de points.
                    </p>
                </div>
            ) : (
            /* Iter-13 — speed bonus timer */
            <div
                className={`rounded-2xl px-4 py-3 flex items-center gap-3 transition-all ${
                    bonusEligible
                        ? "bg-shariti-yellow/10 border border-shariti-yellow/30"
                        : "bg-white/5 border border-white/10"
                }`}
                data-testid="quiz-timer"
            >
                <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                        bonusEligible ? "bg-shariti-yellow/20 text-shariti-yellow" : "bg-white/10 text-white/40"
                    }`}
                >
                    {bonusEligible ? <Zap className="w-4 h-4" /> : <Timer className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/45 mb-0.5">
                        {bonusEligible ? "Bonus rapidité actif" : "Bonus rapidité expiré"}
                    </p>
                    <p className="text-sm font-extrabold text-white">
                        {bonusEligible ? (
                            <>
                                Réponds dans les{" "}
                                <span className="text-shariti-yellow tabular-nums" data-testid="quiz-timer-left">
                                    {timeLeft}s
                                </span>{" "}
                                pour gagner <span className="text-shariti-yellow">+15 pts bonus</span>
                            </>
                        ) : (
                            <>
                                Tu peux toujours valider — sans le bonus rapidité.
                            </>
                        )}
                    </p>
                </div>
                <span
                    className={`text-2xl font-extrabold tabular-nums shrink-0 ${
                        bonusEligible ? "text-shariti-yellow" : "text-white/30"
                    }`}
                >
                    {String(Math.floor(elapsed / 60)).padStart(1, "0")}:
                    {String(elapsed % 60).padStart(2, "0")}
                </span>
            </div>
            )}
            {data.best_score != null && (
                <div className="bg-white/5 rounded-2xl p-3 text-xs text-white/70 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-shariti-orange-light" />
                    Meilleur score :{" "}
                    <span className="font-extrabold text-white">{data.best_score}%</span>
                </div>
            )}
            {data.questions.map((q, qi) => (
                <div
                    key={qi}
                    className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2"
                    data-testid={`asso-quiz-q${qi}`}
                >
                    <p className="text-sm font-extrabold text-white">
                        <span className="text-shariti-orange-light">Q{qi + 1}.</span>{" "}
                        {q.question}
                    </p>
                    <div className="space-y-1.5">
                        {q.options.map((opt, oi) => {
                            const checked = answers[qi] === oi;
                            return (
                                <button
                                    key={oi}
                                    type="button"
                                    onClick={() => {
                                        const next = [...answers];
                                        next[qi] = oi;
                                        setAnswers(next);
                                    }}
                                    data-testid={`asso-quiz-q${qi}-opt${oi}`}
                                    className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-xl transition-all ${
                                        checked
                                            ? "bg-shariti-orange/15 border border-shariti-orange/40"
                                            : "bg-white/[0.03] hover:bg-white/5 border border-transparent"
                                    }`}
                                >
                                    <span
                                        className={`w-4 h-4 rounded-full border-2 shrink-0 mt-0.5 flex items-center justify-center ${
                                            checked
                                                ? "border-shariti-orange bg-shariti-orange"
                                                : "border-white/30"
                                        }`}
                                    >
                                        {checked && (
                                            <span className="w-1.5 h-1.5 bg-white rounded-full" />
                                        )}
                                    </span>
                                    <span className="text-sm text-white/85 flex-1">{opt}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={submit}
                disabled={submitting || answers.some((a) => a < 0)}
                data-testid="asso-quiz-submit"
                className="w-full bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-50"
            >
                {submitting ? "Validation..." : "Valider mes réponses"}
            </button>
        </div>
    );
}

export default function AssociationFiche({
    associationId,
    initialTab = "news",
    previewMode = false,
    onLoaded,
}) {
    const [data, setData] = useState(null);
    const [news, setNews] = useState([]);
    const [resources, setResources] = useState([]);
    const [active, setActive] = useState(initialTab);
    const trackedRef = useRef(new Set());

    useEffect(() => {
        api.get(`/associations/${associationId}`)
            .then((r) => {
                setData(r.data);
                onLoaded?.(r.data);
            })
            .catch(() => setData(false));
        api.get(`/associations/${associationId}/news`)
            .then((r) => setNews(r.data || []))
            .catch(() => setNews([]));
        api.get(`/associations/${associationId}/resources`)
            .then((r) => setResources(r.data || []))
            .catch(() => setResources([]));
    }, [associationId]);

    // Track section views (best-effort, fire-and-forget, deduped per session).
    // Iter-31 — désactivé en previewMode pour ne pas polluer les analytics.
    useEffect(() => {
        if (previewMode || !data || trackedRef.current.has(active)) return;
        trackedRef.current.add(active);
        api.post(`/associations/${associationId}/track`, { section: active }).catch(() => {});
    }, [active, data, associationId, previewMode]);

    // Iter-27.35 — Mark all news as read when the user opens the Actualités tab
    // (skippé en previewMode : la lecture par l'asso ne doit pas compter).
    useEffect(() => {
        if (previewMode || active !== "news") return;
        const unread = news.filter((n) => !n.is_read);
        if (unread.length === 0) return;
        Promise.all(
            unread.map((n) => api.post(`/news/${n.news_id}/read`).catch(() => null)),
        ).then(() => {
            setNews((curr) => curr.map((n) => ({ ...n, is_read: true })));
            // Tell parent layout to refresh the sidebar unread badge
            window.dispatchEvent(new CustomEvent("news-read"));
        });
    }, [active, news, previewMode]);

    // Iter-27.63 — Filter sections to only show those with actual content.
    // Also filter out news with category="gratitude" (those belong to Espace Partenaire).
    const formationNews = news.filter((n) => n.category !== "gratitude");
    const SECTIONS = data && typeof data === "object" ? ALL_SECTIONS.filter((s) => {
        if (s.id === "resources" && resources.length === 0) return false;
        if (s.id === "history" && !data?.history) return false;
        if (s.id === "mission" && !data?.mission) return false;
        if (s.id === "actions" && !data?.actions) return false;
        if (s.id === "victories" && (!data?.victories || data.victories.length === 0)) return false;
        if (s.id === "chiffres_cles" && !data?.chiffres_cles) return false;
        if (s.id === "argumentaire" && !data?.argumentaire) return false;
        if (s.id === "faq" && (!data?.faq || data.faq.length === 0)) return false;
        return true;
    }) : [];

    // Si l'onglet actif n'est plus disponible → fallback sur la 1ère
    useEffect(() => {
        if (SECTIONS.length > 0 && !SECTIONS.find((s) => s.id === active)) {
            setActive(SECTIONS[0].id);
        }
    }, [SECTIONS, active]);

    if (data === null) {
        return (
            <div className="glass-card rounded-3xl p-10 text-center text-white/50">Chargement...</div>
        );
    }
    if (data === false) {
        return (
            <div className="glass-card rounded-3xl p-10 text-center">
                <p className="text-white/70 mb-3">Association introuvable ou non accessible.</p>
                {!previewMode && (
                    <Link
                        to="/associations"
                        className="inline-flex items-center gap-2 text-shariti-orange-light font-bold"
                    >
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="association-fiche">
            {/* Left: video + sticky nav */}
            <div className="lg:col-span-5 space-y-4">
                {data.video_url ? (
                    <VideoEmbed url={data.video_url} />
                ) : (
                    <div className="glass-card rounded-3xl p-8 flex flex-col items-center justify-center min-h-[180px] text-center">
                        <div className="w-12 h-12 rounded-2xl bg-shariti-orange/15 flex items-center justify-center mb-3">
                            <Play className="w-5 h-5 text-shariti-orange" />
                        </div>
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-white/40 mb-1">
                            Vidéo à venir
                        </p>
                        <p className="text-xs text-white/55 font-light max-w-xs">
                            La vidéo de présentation sera bientôt disponible. En attendant, parcours les sections ci-contre.
                        </p>
                    </div>
                )}

                <nav className="glass-card rounded-3xl p-2 flex lg:flex-col gap-1 overflow-x-auto scrollbar-hide">
                    {SECTIONS.map((s) => {
                        const Icon = s.icon;
                        const isActive = active === s.id;
                        const unreadNews =
                            s.id === "news"
                                ? news.filter((n) => !n.is_read).length
                                : 0;
                        return (
                            <button
                                type="button"
                                key={s.id}
                                onClick={() => setActive(s.id)}
                                data-testid={`section-${s.id}`}
                                className={`relative flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all whitespace-nowrap ${
                                    isActive
                                        ? "bg-shariti-orange text-white shadow-lg shadow-shariti-orange/20"
                                        : "text-white/65 hover:text-white hover:bg-white/5"
                                }`}
                            >
                                <Icon className="w-4 h-4 shrink-0" />
                                <span className="flex-1 text-left">{s.label}</span>
                                {unreadNews > 0 && (
                                    <span
                                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold bg-red-500 text-white"
                                        data-testid="news-unread-badge"
                                    >
                                        {unreadNews}
                                    </span>
                                )}
                                {s.id === "resources" && resources.length > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold bg-white/12 text-white/70">
                                        {resources.length}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Right: section content */}
            <div className="lg:col-span-7">
                <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                    className="glass-card rounded-3xl p-5 sm:p-7 min-h-[300px]"
                    data-testid={`section-content-${active}`}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                            <Heart className="w-5 h-5 text-shariti-orange" />
                        </div>
                        <div>
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                                {data.name}
                            </p>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                                {SECTIONS.find((s) => s.id === active)?.label}
                            </h2>
                        </div>
                    </div>
                    <div className="pt-2">
                        {active === "faq" ? (
                            <FaqAccordion items={data.faq} />
                        ) : active === "quiz" ? (
                            <QuizSection associationId={associationId} previewMode={previewMode} />
                        ) : active === "victories" ? (
                            <VictoriesList items={data.victories} />
                        ) : active === "news" ? (
                            <NewsList items={formationNews} />
                        ) : active === "resources" ? (
                            <ResourcesSection items={resources} />
                        ) : (
                            <FormattedText text={data[active]} />
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
